"use client";

import { Heading, Text } from "@pamperedchef/design-system";
import * as styles from "./SectionHeading.module.scss";

export interface SectionHeadingProps {
	eyebrow?: string;
	headline?: string;
	subcopy?: string;
	alignment?: "left" | "center";
	/**
	 * Render the headline in the italic brand display style (`display-md`: 64px
	 * desktop / 48px mobile, weight 500) used by the Figma hero sections (e.g.
	 * SocialProof). Off by default so the standard section headings keep the
	 * smaller upright treatment.
	 */
	displayHeadline?: boolean;
}

/**
 * Shared section header — eyebrow + headline + subcopy. The headline renders
 * in the brand teal serif (Questa) and is left-aligned by default to match the
 * VIP-kit section rhythm ("Your Exclusive Offer", "Explore other kits").
 * Presentational, props-in.
 */
export function SectionHeading({
	eyebrow,
	headline,
	subcopy,
	alignment = "left",
	displayHeadline = false
}: SectionHeadingProps) {
	if (!eyebrow && !headline && !subcopy) return null;

	return (
		<header
			className={styles["section-heading"]}
			data-component="SectionHeading"
			data-alignment={alignment}
		>
			{eyebrow ? (
				<Text
					tag="p"
					styledAs="xsb"
					className={styles["section-heading__eyebrow"]}
				>
					{eyebrow}
				</Text>
			) : null}
			{headline ? (
				<Heading
					tag="h2"
					styledAs="h2"
					className={
						displayHeadline
							? `${styles["section-heading__headline"]} ${styles["section-heading__headline--display"]}`
							: styles["section-heading__headline"]
					}
				>
					{headline}
				</Heading>
			) : null}
			{subcopy ? (
				<Text
					tag="p"
					styledAs="mdr"
					className={styles["section-heading__subcopy"]}
				>
					{subcopy}
				</Text>
			) : null}
		</header>
	);
}
